import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { jobsAPI, countriesAPI, adminAPI, uploadAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ArrowRight, Upload } from 'lucide-react';

// File upload constants
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const AdminJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '', title_fa: '', title_en: '', country_id: '', city_fa: '',
    company_name: '', company_logo_url: '', salary_range: '',
    employment_type: '', experience_level: '', job_description: '',
    requirements: '', benefits: '', application_url: ''
  });

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        await adminAPI.verify();
        await fetchData();
      } catch (error) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [jobsRes, countryRes] = await Promise.all([jobsAPI.getAll(), countriesAPI.getAll()]);
      setJobs(jobsRes.data);
      setCountries(countryRes.data);
    } catch (error) {
      toast.error('خطا در دریافت اطلاعات');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) { 
      toast.error(`حجم فایل نباید بیشتر از ${MAX_FILE_SIZE_MB} مگابایت باشد`); 
      return; 
    }
    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      setFormData({ ...formData, company_logo_url: imageUrl });
      toast.success('لوگو با موفقیت آپلود شد');
    } catch (error) {
      toast.error('خطا در آپلود لوگو');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const jobData = {
        ...formData,
        requirements: formData.requirements.split(',').map(r => r.trim()).filter(r => r),
        benefits: formData.benefits.split(',').map(b => b.trim()).filter(b => b),
      };
      if (editingJob) {
        await jobsAPI.update(formData.id, jobData);
        toast.success('فرصت شغلی با موفقیت ویرایش شد');
      } else {
        jobData.id = Date.now().toString();
        jobData.posted_date = new Date().toISOString();
        await jobsAPI.create(jobData);
        toast.success('فرصت شغلی با موفقیت ایجاد شد');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('خطا در ذخیره فرصت شغلی');
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({ ...job, requirements: job.requirements?.join(', ') || '', benefits: job.benefits?.join(', ') || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این فرصت شغلی اطمینان دارید؟')) return;
    try {
      await jobsAPI.delete(id);
      toast.success('فرصت شغلی با موفقیت حذف شد');
      fetchData();
    } catch (error) {
      toast.error('خطا در حذف فرصت شغلی');
    }
  };

  const resetForm = () => {
    setFormData({ id: '', title_fa: '', title_en: '', country_id: '', city_fa: '', company_name: '', company_logo_url: '', salary_range: '', employment_type: '', experience_level: '', job_description: '', requirements: '', benefits: '', application_url: '' });
    setEditingJob(null);
  };

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#121212] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-[#D4AF37] hover:text-[#F3E5AB]"><ArrowRight size={24} /></Link>
            <h1 className="text-3xl font-light text-white">مدیریت فرصت‌های شغلی</h1>
          </div>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB]">
            <Plus size={20} />فرصت جدید
          </button>
        </div>

        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                  {job.company_logo_url ? <img src={job.company_logo_url} alt={job.company_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-600">💼</div>}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-medium text-white mb-1">{job.title_fa}</h3>
                  <p className="text-sm text-zinc-400 mb-2">{job.company_name} • {job.city_fa}</p>
                  <p className="text-xs text-zinc-500">{job.salary_range}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(job)} className="p-2 text-[#D4AF37] hover:bg-white/5 rounded-lg"><Edit2 size={18} /></button>
                  <button onClick={() => handleDelete(job.id)} className="p-2 text-red-400 hover:bg-white/5 rounded-lg"><Trash2 size={18} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowModal(false)}>
            <div className="glass-effect border border-white/10 rounded-2xl p-8 max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-light text-white mb-6">{editingJob ? 'ویرایش فرصت شغلی' : 'فرصت شغلی جدید'}</h2>
              <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto px-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm text-zinc-300 mb-2">عنوان شغل (فارسی) *</label>
                    <input type="text" name="title_fa" required value={formData.title_fa} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" /></div>
                  <div><label className="block text-sm text-zinc-300 mb-2">نام شرکت *</label>
                    <input type="text" name="company_name" required value={formData.company_name} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none" /></div>
                </div>
                <div><label className="block text-sm text-zinc-300 mb-2">لوگوی شرکت</label>
                  {formData.company_logo_url && <img src={formData.company_logo_url} alt="Logo" className="w-24 h-24 object-cover rounded-lg mb-2" />}
                  <label className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-zinc-300 hover:bg-white/10 cursor-pointer">
                    <Upload size={20} />{uploading ? 'در حال آپلود...' : 'آپلود لوگو'}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label></div>
                <div className="flex gap-4">
                  <button type="submit" className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#F3E5AB]">{editingJob ? 'ذخیره تغییرات' : 'ایجاد فرصت'}</button>
                  <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/5">انصراف</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminJobs;